/**
 * Derived job view helpers (port of UiJob methods in rm-ui/src/types.rs).
 * Pure functions over the REST Job. RM-116: render modes live inside the typed
 * `job_spec` (Blender) rather than a top-level `mode_params` array; non-Blender
 * job types (Empty) carry no modes.
 */
import type { Job, RenderMode } from './schemas'
import { isTerminal } from './jobStatus'

export const JOB_TYPE_LABELS: Record<Job['job_spec']['type'], string> = {
  blender_render: 'Blender',
  upscaling: 'Upscaling',
  empty: 'Empty',
  surroundings: 'Surroundings',
  turntable_frame_bake: 'Turntable Frame',
}

const MODE_LABEL: Record<RenderMode, string> = {
  animation: 'Animation',
  tour: 'Tour',
  turntable: 'Turntable',
  topview: 'TopView',
}

export function displayName(job: Job): string {
  return job.name && job.name.length > 0 ? job.name : 'Untitled Render'
}

export function thumbnailUrl(job: Job): string | null {
  return job.thumbnail_r2_path ? `/api/v1/jobs/${job.id}/thumbnail` : null
}

/** Render modes from the Blender `job_spec` (mirrors Job::render_modes()).
 * Non-Blender job types (Empty) have no modes. */
export function renderModes(job: Job): RenderMode[] {
  if (job.job_spec.type !== 'blender_render') return []
  return job.job_spec.mode_params.map((p) => p.mode).filter(Boolean)
}

export function modeLabels(job: Job): string[] {
  return renderModes(job).map((m) => MODE_LABEL[m] ?? m)
}

export function hasMode(job: Job, mode: RenderMode): boolean {
  return renderModes(job).includes(mode)
}

export function currentMode(job: Job): RenderMode | undefined {
  return renderModes(job)[job.current_mode_index ?? 0]
}

export function totalModes(job: Job): number {
  return renderModes(job).length
}

export function isCancellable(job: Job): boolean {
  return !isTerminal(job.status) && job.status !== 'delivering'
}

/**
 * How long the job has run, as a short human string (e.g. `1h 23min`, `4min 5s`, `12s`).
 * Uses `completed_at - started_at` for finished jobs, or elapsed since `started_at` for
 * in-progress ones (updates on each poll). Returns null before the job starts — queued /
 * preparing jobs have no `started_at` and thus no meaningful duration yet.
 */
export function jobDuration(job: Job): string | null {
  if (!job.started_at) return null
  const startMs = Date.parse(job.started_at)
  if (Number.isNaN(startMs)) return null
  const endMs = job.completed_at ? Date.parse(job.completed_at) : Date.now()
  const ms = endMs - startMs
  if (!Number.isFinite(ms) || ms < 0) return null

  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min ${s}s`
  return `${s}s`
}
